import useSWR, { SWRConfiguration } from 'swr'
import { COMMENT_API_PREFIX } from 'shared/api/constants/api-constants'
import { PAGINATION_ORDER } from 'shared/constants/api'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import { CommentInterface } from '../components/comment/comment.types'
import { CommentSortEnum, CommentsTypeEnum } from '../types'
import { getCommentSortParams } from '../utils/comment-sorting'
import { commentSourceKeyMap } from '../utils/constants'

const PAGE_COMMENTS_DEFAULT_PAGINATION_LIMIT = 10

export const getPageCommentsKey = ({
  commentsType,
  lastCommentId,
  pageId,
  parentId,
  sortOption,
  targetCommentId,
}: {
  parentId: number | null
  commentsType: CommentsTypeEnum
  pageId: number
  lastCommentId: number
  sortOption: CommentSortEnum
  targetCommentId?: number
}): string => {
  const searchParams = new URLSearchParams()
  const commentSourceKey = commentSourceKeyMap[commentsType]
  const apiEndpoint = targetCommentId ? `${COMMENT_API_PREFIX}/focus` : COMMENT_API_PREFIX
  const { sortBy, sortOrder } = getCommentSortParams(sortOption)

  searchParams.set('pagination[limit]', PAGE_COMMENTS_DEFAULT_PAGINATION_LIMIT.toString())
  searchParams.set('pagination[parent][sortOrder]', sortOrder)
  searchParams.set('pagination[children][sortOrder]', PAGINATION_ORDER.ASC)
  searchParams.set('pagination[parent][sortBy]', sortBy)
  searchParams.set('parent', parentId?.toString() ?? 'null')
  searchParams.set(commentSourceKey, pageId.toString())
  if (targetCommentId) {
    searchParams.set('commentId', targetCommentId.toString())
  }
  if (lastCommentId !== 0) {
    searchParams.set('pagination[cursor]', lastCommentId.toString())
  }

  return `${apiEndpoint}?${searchParams.toString()}`
}

interface UsePageCommentsOptions {
  parentId: number | null
  commentsType: CommentsTypeEnum
  pageId: number
  lastCommentId: number
  sortOption: CommentSortEnum
  targetCommentId?: number
  config?: SWRConfiguration<CommentInterface[], Error>
}

export const usePageComments = ({
  parentId,
  commentsType,
  pageId,
  lastCommentId,
  sortOption,
  targetCommentId,
  config,
}: UsePageCommentsOptions) => {
  const { fetcher } = useApiWrapperWithErrorValidation<RequestMethodsEnum.get, CommentInterface[]>({
    method: RequestMethodsEnum.get,
  })

  return useSWR<CommentInterface[]>(
    getPageCommentsKey({
      parentId,
      commentsType,
      pageId,
      lastCommentId,
      sortOption,
      targetCommentId,
    }),
    fetcher,
    config,
  )
}
